import { inject, Injectable, OnDestroy } from '@angular/core';
import { PluginListenerHandle } from '@capacitor/core';
import { TranslateService } from '@ngx-translate/core';
import { Router } from '@angular/router';
import { auditTime, BehaviorSubject, merge, Observable } from 'rxjs';
import CallKit, {
  BackgroundCallModeOptions,
  OngoingCallNotificationOptions,
  OngoingCallNotificationPermissionStatus,
  Order,
  VideoCallPictureInPictureDeactivateOptions,
  VideoCallPictureInPictureOptions,
  VideoCallPictureInPictureState,
} from '@plugins/android_callkit/callkit.plugin';
import { AppStateChangeService } from '@services/app-state-change.service';
import { ConnectAudioService } from '@services/connect-audio.service';
import { NavigationStore } from '@services/navigation.store';
import { NativeVideoCallPictureInPictureService } from '@services/native-video-call-picture-in-picture.service';
import { PlatformService } from '@services/platform.service';
import { SubscriptionsBag } from '@utils/subscriptions-bag';
import { CALL_ORDER_TYPE_NUMBERS } from '@call/call.constants';
import { ActiveAudioCallUiService } from '@call/services/active-audio-call-ui.service';
import { CallSessionService } from '@call/services/call-session.service';

@Injectable({
  providedIn: 'root'
})
export class ActiveAudioCallNativeService implements OnDestroy {
  private readonly activeAudioCallUiService: ActiveAudioCallUiService = inject(ActiveAudioCallUiService);
  private readonly appStateChangeService: AppStateChangeService = inject(AppStateChangeService);
  private readonly callSessionService: CallSessionService = inject(CallSessionService);
  private readonly connectAudioService: ConnectAudioService = inject(ConnectAudioService);
  private readonly navigationStore: NavigationStore = inject(NavigationStore);
  private readonly nativeVideoCallPictureInPictureService: NativeVideoCallPictureInPictureService =
    inject(NativeVideoCallPictureInPictureService);
  private readonly platformService: PlatformService = inject(PlatformService);
  private readonly router: Router = inject(Router);
  private readonly translate: TranslateService = inject(TranslateService);

  private readonly isNativePictureInPictureActiveSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public readonly isNativePictureInPictureActive$: Observable<boolean> = this.isNativePictureInPictureActiveSubject.asObservable();

  private readonly sb: SubscriptionsBag = new SubscriptionsBag();
  private nativeCallKitEndListener: PluginListenerHandle | null = null;
  private nativeCallKitPictureInPictureListener: PluginListenerHandle | null = null;
  private isStarted: boolean = false;
  private isNativeHandlingActive: boolean = false;
  private stoppingNativeSession: object | null = null;
  private syncOperation: Promise<void> | null = null;
  private syncRequestedDuringOperation: boolean = false;
  private nativeVideoPictureInPictureOrderId: number | null = null;
  private nativeVideoPictureInPicturePrepared: boolean = false;

  public get isNativePictureInPictureActive(): boolean {
    return this.isNativePictureInPictureActiveSubject.value;
  }

  ngOnDestroy(): void {
    this.sb.unsubscribeAll();
    void this.unregisterNativeCallKitListeners();
    void this.deactivateForCurrentCall({finalizeAudioSession: true});
  }

  public start(): void {
    if (this.isStarted) return;
    this.isStarted = true;

    void this.registerNativeCallKitListeners();
    this.sb.sub = merge(
      this.activeAudioCallUiService.minimized$,
      this.appStateChangeService.appStateChange$,
      this.connectAudioService.remoteStream$,
      this.connectAudioService.remoteVideoActive$,
      this.connectAudioService.webrtcAudioService.localPreviewStreamChanged$,
      this.connectAudioService.webrtcAudioService.isConnected$,
    ).pipe(
      auditTime(100),
    ).subscribe((): void => {
      void this.syncForCurrentCall();
    });
  }

  public async syncForCurrentCall(): Promise<void> {
    if (this.syncOperation) {
      this.syncRequestedDuringOperation = true;
      await this.syncOperation;
      return;
    }

    do {
      this.syncRequestedDuringOperation = false;
      this.syncOperation = this.syncForCurrentCallInternal();
      try {
        await this.syncOperation;
      } finally {
        this.syncOperation = null;
      }
    } while (this.syncRequestedDuringOperation);
  }

  public async deactivateForCurrentCall(options: VideoCallPictureInPictureDeactivateOptions = {}): Promise<void> {
    const wasNativeHandlingActive: boolean = this.isNativeHandlingActive;

    this.isNativeHandlingActive = false;

    if (wasNativeHandlingActive && this.platformService.isAndroidApp) {
      await this.deactivateAndroidBackgroundCallMode();
    }

    await this.deactivateNativeVideoCallPictureInPicture(options);
  }

  private async syncForCurrentCallInternal(): Promise<void> {
    if (this.shouldDeferToAudioCallScreen()) return;

    if (!this.shouldManageCurrentCall()) {
      await this.deactivateForCurrentCall({
        finalizeAudioSession: !this.hasStartedActiveAudioCall(),
      });
      return;
    }

    await this.enableNativeBackgroundHandling();
    await this.restoreAndroidLocalVideoAfterResumeIfNeeded();
    await this.syncNativeVideoCallPictureInPicture();
  }

  private shouldManageCurrentCall(): boolean {
    return this.activeAudioCallUiService.isMinimized && this.hasStartedActiveAudioCall();
  }

  private hasStartedActiveAudioCall(): boolean {
    if (!this.hasActiveAudioCall()) return false;
    return this.connectAudioService.inStream
      || !!this.connectAudioService.webrtcAudioService.peerConnection
      || this.connectAudioService.webrtcAudioService.streamStartTime > 0;
  }

  private shouldDeferToAudioCallScreen(): boolean {
    if (this.activeAudioCallUiService.isMinimized) return false;
    if (!this.isAudioCallScreenUrl(this.router.url)) return false;
    return this.hasStartedActiveAudioCall();
  }

  private hasActiveAudioCall(): boolean {
    const currentOrderId: number | null = this.normalizeOrderId(this.callSessionService.selectedOrder?.id);
    if (currentOrderId === null) return false;
    return this.callSessionService.selectedOrder?.type === CALL_ORDER_TYPE_NUMBERS.AUDIO_CALL;
  }

  private async registerNativeCallKitListeners(): Promise<void> {
    if (!this.platformService.isAndroidApp && !this.platformService.isIosApp) return;

    if (this.platformService.isAndroidApp && !this.nativeCallKitEndListener) {
      this.nativeCallKitEndListener = await CallKit.addListener('end', async (order: Order): Promise<void> => {
        await this.stopMinimizedCallFromNativeAction(order);
      });
    }

    if (!this.nativeCallKitPictureInPictureListener) {
      this.nativeCallKitPictureInPictureListener = await CallKit.addListener(
        'pictureInPictureChanged',
        (state: VideoCallPictureInPictureState): void => {
          this.isNativePictureInPictureActiveSubject.next(state.active);
          this.syncIosNativeVideoCallPictureInPictureAudioMode();
        },
      );
    }
  }

  private async unregisterNativeCallKitListeners(): Promise<void> {
    const endListener: PluginListenerHandle | null = this.nativeCallKitEndListener;
    const pictureInPictureListener: PluginListenerHandle | null = this.nativeCallKitPictureInPictureListener;
    this.nativeCallKitEndListener = null;
    this.nativeCallKitPictureInPictureListener = null;

    if (endListener) {
      await endListener.remove();
    }
    if (pictureInPictureListener) {
      await pictureInPictureListener.remove();
    }
  }

  private async stopMinimizedCallFromNativeAction(order: Order): Promise<void> {
    if (!this.activeAudioCallUiService.isMinimized) return;
    if (this.isAudioCallScreenUrl(this.router.url)) return;
    if (!this.isEventForCurrentOrder(order.id)) return;
    const identity = this.callSessionService.mediaSessionIdentity;
    if (this.stoppingNativeSession === identity) return;
    const isCurrent = (): boolean => this.callSessionService.mediaSessionIdentity === identity;

    this.stoppingNativeSession = identity;

    try {
      const isCustomer: boolean | null | undefined = this.callSessionService.selectedOrder?.isCustomer;
      if (isCustomer === true) {
        await this.connectAudioService.stopWatchingByCustomer(true);
      } else if (isCustomer === false) {
        await this.connectAudioService.stopSharingForExecutor(true);
      }
    } finally {
      try {
        if (isCurrent()) {
          this.activeAudioCallUiService.clear();
          try {
            await this.deactivateForCurrentCall({finalizeAudioSession: true});
          } finally {
            if (isCurrent()) {
              this.callSessionService.clearSelectedOrder();
              this.navigationStore.clearBackUrl();
            }
          }
        }
      } finally {
        if (this.stoppingNativeSession === identity) this.stoppingNativeSession = null;
      }
    }
  }

  private async enableNativeBackgroundHandling(): Promise<void> {
    if (!this.platformService.isAndroidApp) return;

    await this.activateAndroidBackgroundCallMode();
  }

  private async activateAndroidBackgroundCallMode(): Promise<void> {
    const orderId: number | null = this.normalizeOrderId(this.callSessionService.selectedOrder?.id);
    if (orderId === null) return;

    if (this.appStateChangeService.appIsActive) {
      await this.ensureAndroidOngoingCallNotificationPermission();
    }

    try {
      const options: BackgroundCallModeOptions = {orderId};
      await CallKit.activateBackgroundCallMode(options);
      this.isNativeHandlingActive = true;
    } catch (error) {
      console.error('Failed to activate minimized Android background call mode', error);
    }

    await this.syncAndroidOngoingCallNotification();
  }

  private async deactivateAndroidBackgroundCallMode(): Promise<void> {
    if (!this.platformService.isAndroidApp) return;

    try {
      await CallKit.deactivateBackgroundCallMode();
    } catch (error) {
      console.error('Failed to deactivate minimized Android background call mode', error);
    }

    await this.clearAndroidOngoingCallNotification();
  }

  private async syncAndroidOngoingCallNotification(): Promise<void> {
    if (!this.platformService.isAndroidApp) return;

    const notificationOptions: OngoingCallNotificationOptions | null = this.buildAndroidOngoingCallNotificationOptions();
    if (!notificationOptions || !this.shouldSyncAndroidOngoingCallNotification(notificationOptions)) {
      await this.clearAndroidOngoingCallNotification();
      return;
    }

    const hasNotificationPermission: boolean = this.appStateChangeService.appIsActive
      ? await this.ensureAndroidOngoingCallNotificationPermission()
      : await this.hasAndroidOngoingCallNotificationPermission();
    if (!hasNotificationPermission) {
      await this.clearAndroidOngoingCallNotification();
      return;
    }

    try {
      await CallKit.syncOngoingCallNotification(notificationOptions);
    } catch (error) {
      console.error('Failed to sync minimized Android ongoing call notification', error);
    }
  }

  private shouldSyncAndroidOngoingCallNotification(options: OngoingCallNotificationOptions): boolean {
    if (!this.shouldManageCurrentCall()) return false;
    if (!this.appStateChangeService.appIsActive) return true;
    if (this.activeAudioCallUiService.isMinimized) return true;
    return options.hasLocalVideo;
  }

  private async clearAndroidOngoingCallNotification(): Promise<void> {
    if (!this.platformService.isAndroidApp) return;

    try {
      await CallKit.clearOngoingCallNotification();
    } catch (error) {
      console.error('Failed to clear minimized Android ongoing call notification', error);
    }
  }

  private async hasAndroidOngoingCallNotificationPermission(): Promise<boolean> {
    try {
      const permissionStatus: OngoingCallNotificationPermissionStatus = await CallKit.checkOngoingCallNotificationPermission();
      return permissionStatus.notifications === 'granted';
    } catch (error) {
      console.error('Failed to check minimized Android ongoing call notification permission', error);
      return false;
    }
  }

  private async ensureAndroidOngoingCallNotificationPermission(): Promise<boolean> {
    try {
      const permissionStatus: OngoingCallNotificationPermissionStatus = await CallKit.checkOngoingCallNotificationPermission();
      if (permissionStatus.notifications === 'granted') {
        return true;
      }

      const requestedPermissionStatus: OngoingCallNotificationPermissionStatus =
        await CallKit.requestOngoingCallNotificationPermission();
      return requestedPermissionStatus.notifications === 'granted';
    } catch (error) {
      console.error('Failed to request minimized Android ongoing call notification permission', error);
      return false;
    }
  }

  private async syncNativeVideoCallPictureInPicture(): Promise<void> {
    if (!this.platformService.isAndroidApp && !this.platformService.isIosApp) return;

    const orderId: number | null = this.normalizeOrderId(this.callSessionService.selectedOrder?.id);
    if (orderId === null) {
      await this.deactivateNativeVideoCallPictureInPicture();
      return;
    }

    if (this.platformService.isIosApp) {
      await this.syncIosNativeVideoCallPictureInPicture(orderId);
      return;
    }

    if (!this.shouldUseNativeVideoCallPictureInPicture(orderId)) {
      await this.deactivateNativeVideoCallPictureInPicture();
      return;
    }

    const options: VideoCallPictureInPictureOptions = {
      orderId,
      hasRemoteVideo: true,
      aspectRatioWidth: 9,
      aspectRatioHeight: 16,
    };

    try {
      await CallKit.activateVideoCallPictureInPicture(options);
    } catch (error) {
      console.error('Failed to activate minimized native video call picture-in-picture', error);
    }
  }

  private shouldUseNativeVideoCallPictureInPicture(orderId: number): boolean {
    if (!this.shouldManageCurrentCall()) return false;
    return this.shouldPrepareOrKeepNativeVideoPictureInPicture(orderId);
  }

  private shouldPrepareOrKeepNativeVideoPictureInPicture(orderId: number): boolean {
    this.syncNativeVideoPictureInPictureOrder(orderId);

    const hasActiveRemoteVideo: boolean = this.connectAudioService.remoteVideoActive;
    const hasVideoHistory: boolean = hasActiveRemoteVideo
      || this.connectAudioService.remoteVideoWasActive
      || this.hasLocalVideoForIosNativePictureInPicture();

    if (!hasVideoHistory) {
      this.nativeVideoPictureInPicturePrepared = false;
      return false;
    }

    if (this.nativeVideoPictureInPicturePrepared || this.isNativePictureInPictureActive) {
      return true;
    }

    if (!this.appStateChangeService.appIsActive) return false;

    this.nativeVideoPictureInPicturePrepared = true;
    return true;
  }

  private hasLocalVideoForIosNativePictureInPicture(): boolean {
    if (!this.platformService.isIosApp) return false;
    return this.connectAudioService.isAudioCallVideoEnabled || this.connectAudioService.hasLocalVideoTrack();
  }

  private syncNativeVideoPictureInPictureOrder(orderId: number): void {
    if (this.nativeVideoPictureInPictureOrderId === orderId) return;

    this.nativeVideoPictureInPictureOrderId = orderId;
    this.nativeVideoPictureInPicturePrepared = false;
  }

  private async syncIosNativeVideoCallPictureInPicture(orderId: number): Promise<void> {
    if (!this.shouldUseNativeVideoCallPictureInPicture(orderId)) {
      if (!this.appStateChangeService.appIsActive) return;
      this.connectAudioService.webrtcAudioService.setIosNativeVideoCallPictureInPictureAudioMode(false);
      await this.deactivateNativeVideoCallPictureInPicture();
      return;
    }

    const remoteStream: MediaStream | null = this.connectAudioService.getRemoteStream();
    const localPictureInPictureStream: MediaStream | null = this.resolveIosPictureInPictureLocalStream();
    const fallback = {
      fullName: this.partnerFullName,
      avatarUrl: this.callSessionService.selectedOrder?.partnerAvatar ?? null,
    };

    if (this.connectAudioService.remoteVideoActive && remoteStream) {
      this.syncIosNativeVideoCallPictureInPictureAudioMode();
      await this.nativeVideoCallPictureInPictureService.activateForStream({
        orderId,
        sourceStream: remoteStream,
        aspectRatioWidth: 9,
        aspectRatioHeight: 16,
        preferSpeakerAudio: true,
        fallback,
      });
      return;
    }

    if (localPictureInPictureStream) {
      this.syncIosNativeVideoCallPictureInPictureAudioMode();
      await this.nativeVideoCallPictureInPictureService.activateForStream({
        orderId,
        sourceStream: localPictureInPictureStream,
        aspectRatioWidth: 9,
        aspectRatioHeight: 16,
        preferSpeakerAudio: true,
        fallback,
      });
      return;
    }

    if (this.shouldAttachRemoteStreamToIosPictureInPicture(remoteStream)) {
      this.syncIosNativeVideoCallPictureInPictureAudioMode();
      await this.nativeVideoCallPictureInPictureService.activateForStream({
        orderId,
        sourceStream: remoteStream,
        aspectRatioWidth: 9,
        aspectRatioHeight: 16,
        preferSpeakerAudio: true,
        fallback,
      });
      return;
    }

    this.syncIosNativeVideoCallPictureInPictureAudioMode();
    await this.nativeVideoCallPictureInPictureService.activateForFallback({
      orderId,
      aspectRatioWidth: 9,
      aspectRatioHeight: 16,
      preferSpeakerAudio: true,
      fallback,
    });
  }

  private resolveIosPictureInPictureLocalStream(): MediaStream | null {
    if (!this.hasLocalVideoForIosNativePictureInPicture()) return null;

    const stream: MediaStream | null = this.connectAudioService.getLocalVideoPictureInPictureStream();
    if (!stream) return null;

    const hasLiveVideoTrack: boolean = stream.getVideoTracks().some((track: MediaStreamTrack): boolean => {
      if (track.readyState !== 'live') return false;
      return track.muted !== true;
    });
    return hasLiveVideoTrack ? stream : null;
  }

  private shouldAttachRemoteStreamToIosPictureInPicture(remoteStream: MediaStream | null): remoteStream is MediaStream {
    if (!remoteStream) return false;
    if (this.connectAudioService.remoteVideoActive) return true;
    return this.connectAudioService.remoteVideoWasActive;
  }

  private syncIosNativeVideoCallPictureInPictureAudioMode(): void {
    if (!this.platformService.isIosApp) return;

    const shouldUseDirectPlayback: boolean =
      !this.appStateChangeService.appIsActive || this.isNativePictureInPictureActive;
    this.connectAudioService.webrtcAudioService.setIosNativeVideoCallPictureInPictureAudioMode(shouldUseDirectPlayback);
  }

  private async deactivateNativeVideoCallPictureInPicture(
    options: VideoCallPictureInPictureDeactivateOptions = {},
  ): Promise<void> {
    if (!this.platformService.isAndroidApp && !this.platformService.isIosApp) return;

    this.isNativePictureInPictureActiveSubject.next(false);
    this.nativeVideoPictureInPictureOrderId = null;
    this.nativeVideoPictureInPicturePrepared = false;

    if (this.platformService.isIosApp) {
      this.connectAudioService.webrtcAudioService.setIosNativeVideoCallPictureInPictureAudioMode(false);
      await this.nativeVideoCallPictureInPictureService.deactivate(options);
      return;
    }

    try {
      await CallKit.deactivateVideoCallPictureInPicture();
    } catch (error) {
      console.error('Failed to deactivate minimized native video call picture-in-picture', error);
    }
  }

  private async restoreAndroidLocalVideoAfterResumeIfNeeded(): Promise<void> {
    if (!this.platformService.isAndroidApp) return;
    if (!this.appStateChangeService.appIsActive) return;
    if (!this.connectAudioService.isAudioCallVideoEnabled) return;

    try {
      await this.connectAudioService.restoreCameraForAudioCallIfNeeded();
    } catch (error) {
      console.error('Failed to restore minimized Android local video after resume', error);
    }
  }

  private buildAndroidOngoingCallNotificationOptions(): OngoingCallNotificationOptions | null {
    const orderId: number | null = this.normalizeOrderId(this.callSessionService.selectedOrder?.id);
    if (orderId === null) return null;

    const title: string = this.partnerFullName || this.translate.instant('call.push.title');
    const text: string = this.resolveAndroidOngoingCallNotificationText();
    const endCallLabel: string = this.translate.instant('call.audioCall.alerts.stopCall.buttons.confirm');
    const hasLocalVideo: boolean = this.connectAudioService.isAudioCallVideoEnabled
      && !this.connectAudioService.isScreenSharing
      && this.connectAudioService.hasLocalVideoTrack();
    const startedAtMs: number | null = this.connectAudioService.webrtcAudioService.streamStartTime > 0
      ? this.connectAudioService.webrtcAudioService.streamStartTime
      : null;

    if (startedAtMs !== null) {
      return {
        orderId,
        title,
        text,
        endCallLabel,
        isConnected: this.connectAudioService.webrtcAudioService.isConnected,
        hasLocalVideo,
        startedAtMs,
      };
    }

    return {
      orderId,
      title,
      text,
      endCallLabel,
      isConnected: this.connectAudioService.webrtcAudioService.isConnected,
      hasLocalVideo,
    };
  }

  private get partnerFullName(): string {
    const name: string | null | undefined = this.callSessionService.selectedOrder?.partnerName;
    const surname: string | null | undefined = this.callSessionService.selectedOrder?.partnerSurname;
    return [name, surname].filter(Boolean).join(' ');
  }

  private resolveAndroidOngoingCallNotificationText(): string {
    if (!this.connectAudioService.webrtcAudioService.isConnected
      && this.connectAudioService.webrtcAudioService.streamStartTime > 0) {
      return this.translate.instant('call.audioCall.statusHints.reconnecting');
    }

    if (this.connectAudioService.webrtcAudioService.isConnected) {
      return this.translate.instant('call.push.text');
    }

    if (this.callSessionService.selectedOrder?.isCustomer === true) {
      return this.translate.instant('call.audioCall.statusHints.calling');
    }

    return this.translate.instant('call.callModal.connecting');
  }

  private isEventForCurrentOrder(orderId: number | string | null | undefined): boolean {
    const currentOrderId: number | null = this.normalizeOrderId(this.callSessionService.selectedOrder?.id);
    const incomingOrderId: number | null = this.normalizeOrderId(orderId);
    return currentOrderId !== null && incomingOrderId === currentOrderId;
  }

  private isAudioCallScreenUrl(url: string | null | undefined): boolean {
    if (!url) return false;
    const normalizedUrl: string = url.toLowerCase();
    return normalizedUrl.includes('/audio-call')
      || normalizedUrl.includes('/incoming-call');
  }

  private normalizeOrderId(value: number | string | null | undefined): number | null {
    if (typeof value === 'number' && Number.isFinite(value) && value > 0) return value;
    if (typeof value === 'string') {
      const parsed: number = Number(value);
      if (Number.isFinite(parsed) && parsed > 0) return parsed;
    }
    return null;
  }
}
